import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addDays, fmtRange, key, parse, weekDates } from '../shared/dates';
import { runTrack } from '../shared/streaks';
import { DEFAULT_CFG, emptyState } from '../shared/defaults';
import { balance, makeId, MAX_TITLE, withRunningBalance } from '../shared/ledger';
import { allowancePaid, owedForWeek, PERFECT_TARGET, perfectStreak, weekTotal } from '../shared/week';
import type { Cfg, Days, LedgerEntry, State, Track as TrackName } from '../shared/types';
import { getState, putState, readCache, readToken, saveToken, writeCache } from './api';

/* ---------- tokens ---------- */
const C = {
  bg: '#131f2c',
  surface: '#1b2b3d',
  surfaceHi: '#22374d',
  line: '#2e4763',
  gold: '#f0b429',
  goldDim: '#8a6a22',
  green: '#4fbf87',
  greenDim: '#2b5f48',
  dusty: '#9a6b70',
  miss: '#8ba3ba',   // a marked-but-not-earned day: neutral, not a reprimand
  text: '#e8f0f8',
  muted: '#8ba3ba',
  faint: '#59748f',
};

const FONT = "'Rubik', 'Heebo', 'Segoe UI', system-ui, sans-serif";
const DAY_LETTERS = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
const POLL_MS = 60_000;

/* ---------- small pieces ---------- */
function Slots({ filled, total, color, dim }: { filled: number; total: number; color: string; dim: string }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: i < filled ? color : 'transparent',
            border: `2px solid ${i < filled ? color : dim}`,
            transition: 'background 220ms ease',
          }}
        />
      ))}
    </div>
  );
}

function Track({ label, streak, target, rate }: { label: string; streak: number; target: number; rate: number }) {
  const done = streak >= target;
  const left = target - streak;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div>
        <div style={{ fontSize: 17, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
          {done ? 'נסגר!' : `עוד ${left} ${left === 1 ? 'יום' : 'ימים'} ל־${target * rate} ₪`}
        </div>
      </div>
      <Slots filled={streak} total={target} color={C.green} dim={C.line} />
    </div>
  );
}

function Cell({
  state,
  onClick,
  editable,
  dim,
}: {
  state: 'green' | 'red' | null;
  onClick: () => void;
  editable: boolean;
  dim?: boolean;
}) {
  const bg = state === 'green' ? C.green : state === 'red' ? C.miss : 'transparent';
  const bd = state ? bg : dim ? '#243a52' : C.line;
  return (
    <button
      onClick={editable ? onClick : undefined}
      disabled={!editable}
      aria-label={state === 'green' ? 'ירוק' : state === 'red' ? 'אדום' : 'ריק'}
      style={{
        width: '100%',
        aspectRatio: '1',
        borderRadius: 9,
        background: bg,
        border: `2px solid ${bd}`,
        cursor: editable ? 'pointer' : 'default',
        padding: 0,
        color: C.bg,
        fontWeight: 700,
        fontSize: 15,
        fontFamily: FONT,
        transition: 'background 180ms ease, border-color 180ms ease',
      }}
    >
      {state === 'green' ? '✓' : state === 'red' ? '–' : ''}
    </button>
  );
}

/* ---------- main ---------- */
export default function InbarTracker() {
  const [st, setSt] = useState<State | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [anchor, setAnchor] = useState(new Date());
  const [now, setNow] = useState(new Date());
  const [token, setToken] = useState(readToken);
  const [askToken, setAskToken] = useState(false);
  const [tokenTry, setTokenTry] = useState('');
  const [checking, setChecking] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tab, setTab] = useState<'track' | 'account'>('track');
  const [sign, setSign] = useState<1 | -1>(-1);
  const [amountDraft, setAmountDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const inFlight = useRef(false);

  const parent = token !== '';

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    try {
      const s = await getState();
      if (inFlight.current) return;
      setSt(s);
      writeCache(s);
      setOffline(false);
    } catch {
      setOffline(true);
    }
    setNow(new Date());
  }, []);

  /* load */
  useEffect(() => {
    (async () => {
      try {
        const s = await getState();
        setSt(s);
        writeCache(s);
      } catch {
        setSt(readCache() ?? emptyState());
        setOffline(true);
      }
      setLoading(false);
    })();
  }, []);

  /* poll + refresh on focus */
  useEffect(() => {
    const id = setInterval(refresh, POLL_MS);
    const onFocus = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  /* optimistic write, rebased once on conflict, rolled back on failure */
  const mutate = useCallback(
    async (fn: (s: State) => State) => {
      if (!st || !token) return;
      const previous = st;
      setSt(fn(previous));
      inFlight.current = true;
      let res = await putState(fn(previous), token);
      if (!res.ok && res.kind === 'conflict') {
        const rebased = fn(res.state);
        setSt(rebased);
        res = await putState(rebased, token);
      }
      inFlight.current = false;
      if (res.ok) {
        setSt(res.state);
        writeCache(res.state);
        setErr(null);
        setOffline(false);
      } else {
        setSt(previous);
        if (res.kind === 'unauthorized') {
          saveToken('');
          setToken('');
          setErr('הקוד אינו תקף. היכנסו שוב.');
        } else {
          setErr('לא הצלחתי לשמור. נסו שוב.');
        }
      }
    },
    [st, token]
  );

  const cycle = (dk: string, track: TrackName) =>
    mutate((s) => {
      const cur = s.days[dk]?.[track] ?? null;
      const next = cur === null ? 'green' : cur === 'green' ? 'red' : null;
      const entry = { ...(s.days[dk] || {}) };
      if (next === null) delete entry[track];
      else entry[track] = next;
      const days: Days = { ...s.days, [dk]: entry };
      if (Object.keys(entry).length === 0) delete days[dk];
      return { ...s, days };
    });

  const setCfgVal = <K extends keyof Cfg>(k: K, v: Cfg[K]) =>
    mutate((s) => ({ ...s, cfg: { ...s.cfg, [k]: v } }));

  const tryToken = async () => {
    if (!st || !tokenTry) return;
    setChecking(true);
    const res = await putState(st, tokenTry);
    setChecking(false);
    if (res.ok) {
      setSt(res.state);
      writeCache(res.state);
      saveToken(tokenTry);
      setToken(tokenTry);
      setAskToken(false);
      setTokenTry('');
      setErr(null);
    } else if (!res.ok && res.kind === 'conflict') {
      setSt(res.state);
      saveToken(tokenTry);
      setToken(tokenTry);
      setAskToken(false);
      setTokenTry('');
      setErr(null);
    } else {
      setTokenTry('');
      setErr('הקוד אינו תקף.');
    }
  };

  /* ---------- derived ---------- */
  const cfg = st?.cfg ?? DEFAULT_CFG;
  const days = st?.days ?? {};
  const wk = useMemo(() => weekDates(anchor), [anchor]);
  const today = now;
  const from = parse(cfg.debtStart);
  const horizon = today > wk[6] ? today : wk[6];

  const home = useMemo(
    () => runTrack(days, 'home', cfg.targetHome, cfg.rate, from, horizon),
    [days, cfg, anchor, now]
  );
  const school = useMemo(
    () => runTrack(days, 'school', cfg.targetSchool, cfg.rate, from, horizon),
    [days, cfg, anchor, now]
  );
  const perfect = useMemo(
    () => (st ? perfectStreak(st, from, horizon) : 0),
    [st, days, cfg, anchor, now]
  );

  const inWeek = (dk: string) => dk >= key(wk[0]) && dk <= key(wk[6]);
  const earnedWeek =
    home.awards.filter((a) => inWeek(a.date)).reduce((s, a) => s + a.amount, 0) +
    school.awards.filter((a) => inWeek(a.date)).reduce((s, a) => s + a.amount, 0);
  const pending = (home.streak + school.streak) * cfg.rate;

  /* projection: fill the rest of this week green */
  const projected = useMemo(() => {
    const sim: Days = { ...days };
    for (const d of wk) {
      const dk = key(d);
      if (d < today && dk !== key(today)) continue;
      const e = { ...(sim[dk] || {}) };
      if (!e.home) e.home = 'green';
      if (!e.school && cfg.schoolDays.includes(d.getDay())) e.school = 'green';
      sim[dk] = e;
    }
    const h = runTrack(sim, 'home', cfg.targetHome, cfg.rate, from, wk[6]);
    const s = runTrack(sim, 'school', cfg.targetSchool, cfg.rate, from, wk[6]);
    return (
      h.awards.filter((a) => inWeek(a.date)).reduce((x, a) => x + a.amount, 0) +
      s.awards.filter((a) => inWeek(a.date)).reduce((x, a) => x + a.amount, 0)
    );
  }, [days, cfg, anchor, now]);

  /* debt */
  const weeksElapsed = Math.max(0, Math.floor((today.getTime() - from.getTime()) / 604800000));
  const paid = Math.min(cfg.debtTotal, weeksElapsed * cfg.debtWeekly);
  const debtLeft = Math.max(0, cfg.debtTotal - paid);
  const weeksLeft = Math.ceil(debtLeft / Math.max(1, cfg.debtWeekly));
  const base = debtLeft > 0 ? cfg.base : cfg.baseAfterDebt;

  /* account */
  const ledger = st?.ledger ?? [];
  const bal = balance(ledger);
  const weekKey = key(wk[0]);
  const outings = st?.outings ?? [];
  const outingReady = perfect >= PERFECT_TARGET;

  const claimOuting = () =>
    mutate((s) => {
      if (perfectStreak(s, from, horizon) < PERFECT_TARGET) return s;
      return { ...s, outings: [...s.outings, key(new Date())] };
    });

  const alreadyPaid = st ? allowancePaid(st.ledger, weekKey) : 0;
  const weekWorth = st ? weekTotal(st, wk[0], wk[6], today) : base + earnedWeek;
  const waiting = st ? owedForWeek(st, wk[0], wk[6], today) : weekWorth;

  const payAllowance = () =>
    mutate((s) => {
      // Recomputed from whatever the server holds rather than from this
      // client's view, so a second parent paying the same week settles to zero
      // and this becomes a no-op instead of a double credit.
      const amount = owedForWeek(s, wk[0], wk[6], today);
      if (amount <= 0) return s;
      const entry: LedgerEntry = {
        id: makeId(),
        ts: Date.now(),
        date: key(new Date()),
        amount,
        title: `דמי כיס ${fmtRange(wk)}`,
        kind: 'allowance',
        week: weekKey,
      };
      return { ...s, ledger: [...s.ledger, entry] };
    });

  const amountValue = Number(amountDraft);
  const canAdd = Number.isFinite(amountValue) && amountValue > 0 && titleDraft.trim().length > 0;

  const addEntry = () => {
    if (!canAdd) return;
    const entry: LedgerEntry = {
      id: makeId(),
      ts: Date.now(),
      date: key(new Date()),
      amount: sign * amountValue,
      title: titleDraft.trim().slice(0, MAX_TITLE),
      kind: 'manual',
    };
    mutate((s) => ({ ...s, ledger: [...s.ledger, entry] }));
    setAmountDraft('');
    setTitleDraft('');
  };

  const box = {
    background: C.surface,
    borderRadius: 16,
    padding: 20,
    border: `1px solid ${C.line}`,
    marginBottom: 14,
  };
  const h = { fontSize: 13, color: C.muted, marginBottom: 14, fontWeight: 400 };

  if (loading) {
    return (
      <div
        dir="rtl"
        style={{
          fontFamily: FONT,
          background: C.bg,
          color: C.muted,
          padding: 48,
          textAlign: 'center',
          borderRadius: 16,
        }}
      >
        טוען…
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      style={{
        fontFamily: FONT,
        background: C.bg,
        color: C.text,
        padding: 18,
        borderRadius: 18,
        maxWidth: 560,
        margin: '0 auto',
      }}
    >
      <style>{`button:focus-visible { outline: 2px solid ${C.gold}; outline-offset: 2px; }`}</style>

      {err && <div style={{ ...box, borderColor: C.dusty, color: C.dusty, marginBottom: 14 }}>{err}</div>}

      {/* tabs */}
      <div
        style={{
          display: 'flex',
          gap: 4,
          marginBottom: 14,
          background: C.surface,
          padding: 4,
          borderRadius: 13,
          border: `1px solid ${C.line}`,
        }}
      >
        {([['track', 'מעקב'], ['account', 'חשבון']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: 9,
              border: 'none',
              cursor: 'pointer',
              fontFamily: FONT,
              fontSize: 15,
              fontWeight: tab === id ? 600 : 400,
              background: tab === id ? C.surfaceHi : 'transparent',
              color: tab === id ? C.text : C.muted,
              transition: 'background 160ms ease, color 160ms ease',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'track' && (
        <>
      {/* hero */}
      <div style={{ ...box, background: C.surfaceHi }}>
        <div style={h}>הרצפים שלך</div>
        <div style={{ display: 'grid', gap: 18 }}>
          <Track label="בבית" streak={home.streak} target={cfg.targetHome} rate={cfg.rate} />
          <Track label="בבית הספר" streak={school.streak} target={cfg.targetSchool} rate={cfg.rate} />
        </div>

        <div
          style={{
            borderTop: `1px solid ${C.line}`,
            marginTop: 20,
            paddingTop: 18,
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: 13, color: C.muted }}>
              {waiting > 0 ? 'מחכה לך בשישי' : 'הכל הועבר לחשבון'}
            </div>
            <div style={{ fontSize: 12, color: C.faint, marginTop: 4 }}>
              {alreadyPaid > 0
                ? `${weekWorth} השבוע · ${alreadyPaid} כבר בחשבון`
                : `${base} דמי כיס + ${earnedWeek} שהרווחת`}
            </div>
          </div>
          <div
            style={{
              fontSize: 46,
              fontWeight: 700,
              color: waiting > 0 ? C.gold : C.faint,
              lineHeight: 1,
            }}
          >
            {waiting}
            <span style={{ fontSize: 22, fontWeight: 400, marginRight: 4 }}>₪</span>
          </div>
        </div>

        {pending > 0 && (
          <div style={{ fontSize: 13, color: C.goldDim, marginTop: 12 }}>ועוד {pending} ₪ ברצף שעוד לא נסגר</div>
        )}

        {parent && (
          <button
            onClick={payAllowance}
            disabled={waiting <= 0}
            style={{
              width: '100%',
              marginTop: 16,
              padding: '12px 0',
              borderRadius: 10,
              fontFamily: FONT,
              fontSize: 15,
              fontWeight: 500,
              cursor: waiting > 0 ? 'pointer' : 'default',
              background: waiting > 0 ? C.gold : 'transparent',
              border: `1px solid ${waiting > 0 ? C.gold : C.line}`,
              color: waiting > 0 ? C.bg : C.faint,
              transition: 'background 180ms ease',
            }}
          >
            {waiting > 0 ? `העבר ${waiting} ₪ לחשבון` : 'הועבר לחשבון ✓'}
          </button>
        )}
      </div>

      {/* week grid */}
      <div style={box}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: C.muted }}>{fmtRange(wk)}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setAnchor(addDays(anchor, -7))} style={navBtn} aria-label="שבוע קודם">
              →
            </button>
            <button onClick={() => setAnchor(addDays(anchor, 7))} style={navBtn} aria-label="שבוע הבא">
              ←
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '62px repeat(7, 1fr)', gap: 6, alignItems: 'center' }}>
          <div />
          {wk.map((d, i) => (
            <div
              key={i}
              style={{
                textAlign: 'center',
                fontSize: 13,
                color: key(d) === key(today) ? C.gold : C.muted,
                fontWeight: key(d) === key(today) ? 700 : 400,
              }}
            >
              {DAY_LETTERS[d.getDay()]}
            </div>
          ))}

          <div style={{ fontSize: 14, color: C.muted }}>בית</div>
          {wk.map((d, i) => (
            <Cell
              key={i}
              state={days[key(d)]?.home ?? null}
              editable={parent}
              onClick={() => cycle(key(d), 'home')}
            />
          ))}

          <div style={{ fontSize: 14, color: C.muted }}>ביה״ס</div>
          {wk.map((d, i) => (
            <Cell
              key={i}
              state={days[key(d)]?.school ?? null}
              editable={parent}
              dim={!cfg.schoolDays.includes(d.getDay())}
              onClick={() => cycle(key(d), 'school')}
            />
          ))}
        </div>

        {projected > earnedWeek && (
          <div style={{ marginTop: 16, fontSize: 14, color: C.muted }}>
            אם שאר השבוע ירוק —{' '}
            <span style={{ color: C.gold, fontWeight: 500 }}>
              {Math.max(0, base + projected - alreadyPaid)} ₪
            </span>{' '}
            בשישי
          </div>
        )}
      </div>

      {/* restaurant goal */}
      <div style={box}>
        <div style={h}>5 ימים רצופים ירוקים בשניהם — יציאה לבחירתך</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Slots filled={Math.min(perfect, PERFECT_TARGET)} total={PERFECT_TARGET} color={C.gold} dim={C.line} />
          <div
            style={{
              fontSize: 15,
              color: outingReady ? C.gold : C.muted,
              fontWeight: outingReady ? 700 : 400,
            }}
          >
            {outingReady ? 'הגעת!' : `${perfect} מתוך ${PERFECT_TARGET}`}
          </div>
        </div>

        {outings.length > 0 && (
          <div style={{ fontSize: 12, color: C.faint, marginTop: 12 }}>
            {outings.length === 1 ? 'יציאה אחת עד היום' : `${outings.length} יציאות עד היום`}
            {' · '}
            האחרונה ב־{parse(outings[outings.length - 1]).getDate()}.
            {parse(outings[outings.length - 1]).getMonth() + 1}
          </div>
        )}

        {parent && outingReady && (
          <button
            onClick={claimOuting}
            style={{
              width: '100%',
              marginTop: 14,
              padding: '12px 0',
              borderRadius: 10,
              fontFamily: FONT,
              fontSize: 15,
              fontWeight: 500,
              cursor: 'pointer',
              background: C.gold,
              border: `1px solid ${C.gold}`,
              color: C.bg,
            }}
          >
            מימשנו — התחל רצף חדש
          </button>
        )}
      </div>

      {/* debt */}
      <div style={box}>
        <div style={h}>החוב על הקיר</div>
        <div style={{ height: 12, borderRadius: 6, background: '#243a52', overflow: 'hidden' }}>
          <div
            style={{
              width: `${cfg.debtTotal > 0 ? (paid / cfg.debtTotal) * 100 : 100}%`,
              height: '100%',
              background: C.gold,
              transition: 'width 400ms ease',
            }}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 14 }}>
          <span style={{ color: C.muted }}>
            שילמת {paid} מתוך {cfg.debtTotal} ₪
          </span>
          <span style={{ color: debtLeft === 0 ? C.green : C.text }}>
            {debtLeft === 0 ? 'נגמר!' : `עוד ${weeksLeft} ${weeksLeft === 1 ? 'שבוע' : 'שבועות'}`}
          </span>
        </div>
      </div>

        </>
      )}

      {tab === 'account' && (
        <>
          <div style={{ ...box, background: C.surfaceHi }}>
            <div style={h}>היתרה שלך</div>
            <div style={{ fontSize: 46, fontWeight: 700, color: bal < 0 ? C.dusty : C.gold, lineHeight: 1 }}>
              {bal}
              <span style={{ fontSize: 22, fontWeight: 400, marginRight: 4 }}>₪</span>
            </div>
          </div>

          {parent && (
            <div style={box}>
              <div style={h}>הוספת תנועה</div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {([[1, 'הפקדה'], [-1, 'הוצאה']] as const).map(([v, label]) => (
                  <button
                    key={v}
                    onClick={() => setSign(v)}
                    style={{
                      flex: 1,
                      padding: '9px 0',
                      borderRadius: 8,
                      fontFamily: FONT,
                      fontSize: 14,
                      cursor: 'pointer',
                      background: sign === v ? C.surfaceHi : 'transparent',
                      border: `1px solid ${sign === v ? (v === 1 ? C.green : C.dusty) : C.line}`,
                      color: sign === v ? (v === 1 ? C.green : C.dusty) : C.muted,
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                <input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  maxLength={MAX_TITLE}
                  placeholder="על מה?"
                  style={{ ...input, width: 'auto' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="number"
                    min={1}
                    inputMode="numeric"
                    value={amountDraft}
                    onChange={(e) => setAmountDraft(e.target.value)}
                    placeholder="סכום"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') addEntry();
                    }}
                    style={{ ...input, width: 'auto', flex: 1 }}
                  />
                  <button
                    onClick={addEntry}
                    disabled={!canAdd}
                    style={{
                      ...navBtn,
                      padding: '7px 20px',
                      background: canAdd ? C.surfaceHi : 'transparent',
                      color: canAdd ? C.text : C.faint,
                      cursor: canAdd ? 'pointer' : 'default',
                    }}
                  >
                    הוסף
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={box}>
            <div style={h}>היסטוריה</div>
            {ledger.length === 0 ? (
              <div style={{ fontSize: 14, color: C.faint }}>עוד אין תנועות</div>
            ) : (
              <div style={{ display: 'grid', gap: 2 }}>
                {withRunningBalance(ledger).map((e, i) => (
                  <div
                    key={e.id}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 0',
                      borderTop: i === 0 ? 'none' : `1px solid ${C.line}`,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.title}
                      </div>
                      <div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>
                        {parse(e.date).getDate()}.{parse(e.date).getMonth() + 1}
                      </div>
                    </div>
                    <div style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: e.amount >= 0 ? C.green : C.dusty,
                        }}
                      >
                        {e.amount >= 0 ? '+' : '−'}
                        {Math.abs(e.amount)} ₪
                      </div>
                      <div
                        dir="ltr"
                        style={{ fontSize: 12, color: C.faint, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}
                      >
                        ({e.before} → {e.after})
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {offline && (
        <div style={{ textAlign: 'center', fontSize: 12, color: C.faint, paddingBottom: 8 }}>
          אין חיבור — מוצג המצב האחרון שנשמר
        </div>
      )}

      {/* parent mode */}
      <div style={{ textAlign: 'center', paddingTop: 4, paddingBottom: 4 }}>
        {!parent && !askToken && (
          <button onClick={() => setAskToken(true)} style={linkBtn}>
            מצב הורים
          </button>
        )}
        {askToken && !parent && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>
            <input
              type="password"
              value={tokenTry}
              onChange={(e) => setTokenTry(e.target.value)}
              placeholder="קוד"
              style={input}
              onKeyDown={(e) => {
                if (e.key === 'Enter') tryToken();
              }}
            />
            <button onClick={tryToken} disabled={checking} style={navBtn}>
              {checking ? '…' : 'כניסה'}
            </button>
            <button
              onClick={() => {
                setAskToken(false);
                setTokenTry('');
              }}
              style={linkBtn}
            >
              ביטול
            </button>
          </div>
        )}
        {parent && (
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center' }}>
            <button onClick={() => setSettingsOpen(!settingsOpen)} style={linkBtn}>
              {settingsOpen ? 'סגור הגדרות' : 'הגדרות'}
            </button>
            <button
              onClick={() => {
                saveToken('');
                setToken('');
                setSettingsOpen(false);
              }}
              style={linkBtn}
            >
              יציאה
            </button>
          </div>
        )}
      </div>

      {parent && tab === 'track' && (
        <div style={{ ...box, marginTop: 10, background: 'transparent', borderStyle: 'dashed' }}>
          <div style={{ fontSize: 13, color: C.muted }}>לחיצה על ריבוע מחליפה: ריק → ירוק → אדום → ריק</div>
        </div>
      )}

      {parent && settingsOpen && (
        <div style={box}>
          <div style={h}>הגדרות</div>
          <div style={{ display: 'grid', gap: 12 }}>
            <Num label="₪ ליום ירוק" v={cfg.rate} on={(x) => setCfgVal('rate', x)} />
            <Num label="יעד רצף — בית" v={cfg.targetHome} min={1} on={(x) => setCfgVal('targetHome', x)} />
            <Num label="יעד רצף — בית ספר" v={cfg.targetSchool} min={1} on={(x) => setCfgVal('targetSchool', x)} />
            <Num label="דמי כיס בתקופת החוב" v={cfg.base} on={(x) => setCfgVal('base', x)} />
            <Num label="דמי כיס אחרי החוב" v={cfg.baseAfterDebt} on={(x) => setCfgVal('baseAfterDebt', x)} />
            <Num label="סכום החוב" v={cfg.debtTotal} on={(x) => setCfgVal('debtTotal', x)} />
            <Num label="ניכוי שבועי" v={cfg.debtWeekly} min={1} on={(x) => setCfgVal('debtWeekly', x)} />
            <label style={row}>
              <span style={{ color: C.muted, fontSize: 14 }}>תחילת החוב</span>
              <input
                type="date"
                value={cfg.debtStart}
                onChange={(e) => e.target.value && setCfgVal('debtStart', e.target.value)}
                style={input}
              />
            </label>
            <div>
              <div style={{ color: C.muted, fontSize: 14, marginBottom: 8 }}>ימי לימודים</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {DAY_LETTERS.map((l, i) => {
                  const on = cfg.schoolDays.includes(i);
                  return (
                    <button
                      key={i}
                      onClick={() =>
                        setCfgVal('schoolDays', on ? cfg.schoolDays.filter((x) => x !== i) : [...cfg.schoolDays, i])
                      }
                      style={{
                        flex: 1,
                        padding: '8px 0',
                        borderRadius: 8,
                        fontFamily: FONT,
                        fontSize: 14,
                        background: on ? C.surfaceHi : 'transparent',
                        border: `1px solid ${on ? C.gold : C.line}`,
                        color: on ? C.gold : C.muted,
                        cursor: 'pointer',
                      }}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const navBtn: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${C.line}`,
  color: C.muted,
  borderRadius: 8,
  padding: '6px 12px',
  cursor: 'pointer',
  fontFamily: FONT,
  fontSize: 14,
};
const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: C.faint,
  cursor: 'pointer',
  fontFamily: FONT,
  fontSize: 13,
  padding: 6,
};
const input: React.CSSProperties = {
  background: C.bg,
  border: `1px solid ${C.line}`,
  color: C.text,
  borderRadius: 8,
  padding: '7px 10px',
  fontFamily: FONT,
  fontSize: 14,
  width: 120,
};
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between' };

/* commits on blur / Enter so a settings edit is one write, not one per keystroke */
function Num({ label, v, on, min = 0 }: { label: string; v: number; on: (n: number) => void; min?: number }) {
  const [draft, setDraft] = useState(String(v));
  useEffect(() => setDraft(String(v)), [v]);
  const commit = () => {
    const n = Number(draft);
    if (Number.isFinite(n) && n >= min) {
      if (n !== v) on(n);
    } else setDraft(String(v));
  };
  return (
    <label style={row}>
      <span style={{ color: C.muted, fontSize: 14 }}>{label}</span>
      <input
        type="number"
        min={min}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        style={input}
      />
    </label>
  );
}
